import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useFleetApi from './useFleetApi';
import WidgetPanel from '../../ui/WidgetPanel';
// The null-aware formatter shared with the analytics tiles: unknown -> "—".
import { num } from '../../Analytics/metricValue';
import '../OpsShared.css';
import '../../Analytics/pages/AnalyticsShared.css'; // analytics-chip filter chips
import '../../../styles/data-table.css';            // the shared Customers list table
import './FleetReports.css';

const STATUS = [
  ['all', 'All'], ['active', 'Active'], ['in_service', 'In service'], ['retired', 'Retired'],
];
const TYPES = [
  ['all', 'All types'], ['truck', 'Truck'], ['van', 'Van'], ['car', 'Car'],
  ['sprayer', 'Sprayer'], ['trailer', 'Trailer'], ['other', 'Other'],
];

const odometer = (v) => {
  const n = num(v);
  return n === null ? '—' : n.toLocaleString();
};

export default function VehiclesPage() {
  const { listVehicles, deleteVehicle } = useFleetApi();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const data = await listVehicles();
      setRows(Array.isArray(data) ? data : (data.results || []));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => rows.filter(v => {
    if (status !== 'all' && v.status !== status) return false;
    if (type !== 'all' && (v.type || 'other') !== type) return false;
    if (q) {
      const blob = `${v.unit_code} ${v.make} ${v.model} ${v.year} ${v.vin} ${v.plate}`.toLowerCase();
      if (!blob.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [rows, q, status, type]);

  const onDelete = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    await deleteVehicle(id);
    await load();
  };

  return (
    <div className="ops-page">
      <header className="ops-head">
        <div>
          <h1>Vehicles</h1>
          <div className="ops-muted">Every unit in the fleet. Search or filter, then open one to see service, insurance, and inspections.</div>
        </div>
        <div className="ops-actions">
          <input
            className="ops-search"
            placeholder="Search unit, make, VIN, plate…"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
          <button className="ops-btn" onClick={() => navigate('new')}>+ New vehicle</button>
        </div>
      </header>

      <div className="analytics-chip-row" role="tablist" aria-label="Status">
        {STATUS.map(([k, l]) => (
          <button
            key={k}
            type="button"
            className={`analytics-chip analytics-chip--button ${status === k ? 'is-active' : ''}`}
            onClick={() => setStatus(k)}
          >
            {l}
          </button>
        ))}
        <span className="ops-chip-divider" aria-hidden="true" />
        {TYPES.map(([k, l]) => (
          <button
            key={k}
            type="button"
            className={`analytics-chip analytics-chip--button ${type === k ? 'is-active' : ''}`}
            onClick={() => setType(k)}
          >
            {l}
          </button>
        ))}
      </div>

      <WidgetPanel className="gt-table-shell" padded={false}>
        <div className="gt-table-scroll">
          <div className="gt-table-wrapper">
            <table className="gt-table">
              <thead>
                <tr>
                  <th>Unit</th><th>Make</th><th>Model</th><th className="num">Year</th>
                  <th>VIN</th><th>Plate</th><th>Status</th><th className="num">Odometer</th><th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className="gt-row gt-row--static"><td className="gt-cell" colSpan={9}><div className="gt-empty">Loading…</div></td></tr>
                ) : filtered.length === 0 ? (
                  <tr className="gt-row gt-row--static"><td className="gt-cell" colSpan={9}><div className="gt-empty">No vehicles match. Click “New vehicle” to add one.</div></td></tr>
                ) : filtered.map(v => (
                  <tr key={v.id} className="gt-row" onClick={() => navigate(`${v.id}`)}>
                    <td className="gt-cell mono">{v.unit_code || '—'}</td>
                    <td className="gt-cell name-col">{v.make || '—'}</td>
                    <td className="gt-cell">{v.model || '—'}</td>
                    <td className="gt-cell num">{v.year || '—'}</td>
                    <td className="gt-cell mono" title={v.vin || ''}>{v.vin || '—'}</td>
                    <td className="gt-cell mono" title={v.plate || ''}>{v.plate || '—'}</td>
                    <td className="gt-cell cap">{v.status || '—'}</td>
                    <td className="gt-cell num">{odometer(v.odometer)}</td>
                    <td className="gt-cell" onClick={(e) => e.stopPropagation()}>
                      <div className="gt-actions">
                        <Link to={`${v.id}`}>View</Link>
                        <Link to={`${v.id}/edit`}>Edit</Link>
                        <button type="button" className="gt-link danger" onClick={() => onDelete(v.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="gt-table-footer">
          <div className="gt-table-count">
            Showing <strong>{filtered.length}</strong> of <strong>{rows.length}</strong> vehicles
          </div>
        </div>
      </WidgetPanel>
    </div>
  );
}
